import { Slider } from "@components/atoms/Slider"
import { FilmTile } from "@components/film/FilmTile"
import { Button } from "@components/ui/Button"
import { isPastEvent } from "@utils/past-event"
import { slugify } from "@utils/slugify"
import { tv } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { useEffect, useState } from "react"

export const meta: BlockMeta = {
  title: "Film List",
  description: "A grid of film tiles — either individual sessions, or whole films",
  category: "media",
  tags: ["#subpage", "#homepage", "#featured"],
  icon: "editor-video",
  keywords: ["film grid slider"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("content/film-list", (props) => {
  let [tiles, setTiles] = useState(props.filmQuery?.items)

  useEffect(() => {
    if (props.filmQuery?.parameters?.mode === "sessions") {
      if (["Future", "Today"].includes(props.filmQuery?.parameters?.sessionQuery?.when)) {
        // Strip out past events
        setTiles(props.filmQuery?.items?.filter((item) => !isPastEvent({ film: item.film, session: item.session! })))
      }
    }
  }, [props.filmQuery])

  if (props.filmQuery?.parameters.mode !== "custom" && props.filmQuery?.parameters?.limit) {
    tiles = tiles?.slice(0, props.filmQuery?.parameters?.limit)
  }

  const styles = createStyles({
    perRow: tiles?.length === 3 ? 3 : tiles?.length === 4 ? 4 : "auto",
  })
  const [heading] = useInlineEditableValue<string>("heading")
  const hasButton = !!props.seeMoreLink?.url

  if (!tiles?.length) return null

  return (
    <div className={styles.wrapper()} data-mixpanel-block="Film List" data-mixpanel-heading={heading}>
      <a id={slugify(heading)} />
      <div className="w-grid mx-auto flex justify-between items-center mb-3 gap-4">
        <div className="type-title-m w-[20em] md:w-auto text-pretty flex">
          <EditableText as="h2" store="heading" defaultValue="Latest Films" className="" plainText />
        </div>
        {hasButton && (
          <div className="flex items-end flex-none">
            <Button
              href={props.seeMoreLink?.url}
              variant="default"
              rightIcon="chevronRight"
              data-mixpanel-trigger="See More Link"
            >
              {props.seeMoreLink?.title}
            </Button>
          </div>
        )}
      </div>

      <Slider arrows gridInsert scrollerClass="flex gap-grid-gutter px-grid-margin-full">
        {tiles?.map((item, i) => (
          <div key={i} className={styles.item()}>
            <FilmTile film={item.film} session={item.session ?? undefined} />
          </div>
        ))}
      </Slider>
    </div>
  )
})

const createStyles = tv({
  slots: {
    wrapper: "py-4",
    heading: "type-title-m text-pretty mb-3",
    item: "w-cols-8 sm:w-cols-7 flex-none py-[10px]",
  },
  variants: {
    perRow: {
      3: {
        item: "md:w-cols-4",
      },
      4: {
        item: "md:w-cols-3",
      },
      auto: {
        item: "md:w-cols-3 xl:w-cols-3",
      },
    },
  },
})
